import { BaseModernLayouter } from './BaseModernLayouter';
import { LayoutData, SpacingMode, SpiralAxisType } from '../LayoutData';
import { UpVectorMode, AlignmentMode } from '../../field/FieldTypes';
import { POI } from '../../poi/POI';

// Import our modern layouters (JS files)
declare const ModernLayouters: any;

export class ModernSpiralLayouter extends BaseModernLayouter {
    constructor() {
        super(
            'modern-spiral', 
            'Modern Spiral Layouter with Configurable Parameters',
            {
                modernSpiralFixedSpacing: 1.0,
                modernFlip180: false,
                modernSpiralGrowth: 2.0,
                modernSpiralStartDistance: 0.0,
                modernSpiralTurns: 2.0,
                modernSpiralMaxRadius: 3.0
            },
            '1d',
            [SpacingMode.Fixed]
        );
    }

    // ========================================
    // IMPLEMENTATION OF ABSTRACT METHODS
    // ========================================

    protected getModernLayouterFunction(): (config: any) => any[] {
        return ModernLayouters.spiral;
    }

    protected createConfig(pois: POI[], layoutData: LayoutData): any {
        // Convert legacy spiralAxis enum to layoutDirection vector
        const spiralAxis = layoutData.spiralAxis?.value || SpiralAxisType.Y;
        const layoutDirection = this.convertSpiralAxisToDirection(spiralAxis);
        
        return {
            objectCount: pois.length,
            spacingMode: this.getSpacingModeString(layoutData),
            spacing: this.getParameter(layoutData, 'modernSpiralFixedSpacing', 1.0),
            pathType: 'spiral',
            orientation: layoutData.alignmentMode?.value === AlignmentMode.Aligned ? 'aligned' : 'perpendicular',
            upMode: layoutData.upVectorMode?.value === UpVectorMode.PathUp ? 'path' : 'world',
            flip180: this.getFlipParameter(layoutData),
            layoutDirection: layoutDirection,
            spiralConfig: {
                spiralTurns: this.getParameter(layoutData, 'modernSpiralTurns', 2.0),
                maxRadius: this.getParameter(layoutData, 'modernSpiralMaxRadius', 3.0),
                growthRate: this.getParameter(layoutData, 'modernSpiralGrowth', 2.0),
                startDistance: this.getParameter(layoutData, 'modernSpiralStartDistance', 0.0)
            },
            time: 0
        };
    }

    protected getLayouterName(): string {
        return 'ModernSpiralLayouter';
    }

    validateParameters(layoutData: LayoutData): boolean {
        const spacing = this.getParameter(layoutData, 'modernSpiralFixedSpacing', 1.0);
        const turns = this.getParameter(layoutData, 'modernSpiralTurns', 2.0);
        const maxRadius = this.getParameter(layoutData, 'modernSpiralMaxRadius', 3.0);
        const growthRate = this.getParameter(layoutData, 'modernSpiralGrowth', 2.0);
        
        return spacing > 0 && turns > 0 && maxRadius > 0 && growthRate > 0;
    }

    /**
     * Convert legacy SpiralAxisType enum to modern layoutDirection vector
     */
    private convertSpiralAxisToDirection(spiralAxis: SpiralAxisType): { x: number, y: number, z: number } {
        switch (spiralAxis) {
            case SpiralAxisType.X:
                return { x: 1, y: 0, z: 0 };
            case SpiralAxisType.NegativeX:
                return { x: -1, y: 0, z: 0 };
            case SpiralAxisType.Y:
                return { x: 0, y: 1, z: 0 };
            case SpiralAxisType.NegativeY:
                return { x: 0, y: -1, z: 0 };
            case SpiralAxisType.Z:
                return { x: 0, y: 0, z: 1 };
            case SpiralAxisType.NegativeZ:
                return { x: 0, y: 0, z: -1 };
            default:
                return { x: 0, y: 1, z: 0 }; // Default to Y-axis
        }
    }


}